import { Component, ChangeDetectionStrategy, input, output, signal, computed } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faArrowLeft, faChevronRight, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FormsModule } from '@angular/forms';

export interface PanelItem {
  id: number;
  label: string;
  permalink: string;
}

export type PanelVariant = 'subjects' | 'specializations';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dottnet-sidenav-panel',
  templateUrl: './sidenav-panel.component.html',
  styleUrls: ['./sidenav-panel.component.scss'],
  imports: [
    TranslatePipe,
    FaIconComponent,
    FormsModule,
  ],
})
export class SidenavPanelComponent {
  // FontAwesome icons
  readonly faArrowLeft = faArrowLeft;
  readonly faChevronRight = faChevronRight;
  readonly faMagnifyingGlass = faMagnifyingGlass;

  // Input variables
  readonly items = input<PanelItem[]>([]);
  readonly variant = input<PanelVariant>('subjects');

  // Translation key helper
  t(key: string): string {
    return `dottnet.sidenav.${this.variant()}.${key}`;
  }

  // Output events
  readonly backClick = output<void>();
  readonly itemClick = output<PanelItem>();
  readonly showAllClick = output<void>();
  readonly searchSubmit = output<string>();

  // Search input value
  searchValue = signal<string>('');

  // State for showing all items
  showAllItems = signal<boolean>(false);

  // Filtered items based on search
  filteredItems = computed(() => {
    const search = this.searchValue().toLowerCase().trim();
    if (!search) {
      return this.items();
    }
    return this.items().filter(item =>
      item.label.toLowerCase().includes(search)
    );
  });

  onBackClick(): void {
    this.backClick.emit();
  }

  onItemClick(item: PanelItem): void {
    this.itemClick.emit(item);
  }

  onShowAllClick(): void {
    this.showAllItems.set(!this.showAllItems());
  }

  onSearchSubmit(): void {
    if (this.searchValue().trim()) {
      this.searchSubmit.emit(this.searchValue().trim());
    }
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSearchSubmit();
    }
  }
}
